// m4-recently-played-log.js — recently-played log (ARD M4 contract).
// Exposes window.M4 = { subscribe, getLog, onLogUpdate }.
// (onStationChange removed — GLM Batch C, Decision D1. Track logging flows only
//  through M6.onTrackChange, which fires an immediate poll on station switch.)

window.M4 = (() => {
  // ---------------------------------------------------------------------------
  // Module state
  // ---------------------------------------------------------------------------

  const KEY_LOG      = 'radio.recentLog';
  const MAX_LOG_SIZE = 50;

  let _log          = [];     // in-memory log; re-synced from localStorage on every read
  let _logUpdateCbs = [];     // multi-listener UI update callbacks
  let _storageOk    = null;   // null = not yet checked

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  function _storageAvailable() {
    if (_storageOk !== null) return _storageOk;
    try {
      localStorage.setItem('radio._probe', '1');
      localStorage.removeItem('radio._probe');
      _storageOk = true;
    } catch (e) {
      _storageOk = false;
    }
    return _storageOk;
  }

  function _loadLog() {
    _log = [];
    if (!_storageAvailable()) return;
    try {
      const raw = localStorage.getItem(KEY_LOG);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        _log = parsed.filter(e => typeof e === 'string' && e.trim() !== '').slice(0, MAX_LOG_SIZE);
      }
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M4', fn: '_loadLog', inputs: {}, output: null, durationMs: 0, error: e });
      _log = [];
    }
  }

  function _saveLog() {
    if (!_storageAvailable()) return;
    try {
      localStorage.setItem(KEY_LOG, JSON.stringify(_log));
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M4', fn: '_saveLog', inputs: {}, output: null, durationMs: 0, error: e });
    }
  }

  /**
   * Adds an entry to the log.
   * Skips empty/null strings and duplicates at position 0.
   * Caps the log at MAX_LOG_SIZE, persists, and fires the UI callback.
   */
  function _addToLog(metadata) {
    _loadLog();

    if (!metadata || metadata.trim() === '') return;
    if (_log[0] === metadata) {
      // Same track still playing — notify UI without adding duplicate
      _logUpdateCbs.forEach(cb => cb([..._log]));
      return;
    }

    _log.unshift(metadata);
    if (_log.length > MAX_LOG_SIZE) _log.pop();

    _saveLog();

    _logUpdateCbs.forEach(cb => cb([..._log]));
  }

  // ---------------------------------------------------------------------------
  // Public interface (ARD M4 contract)
  // ---------------------------------------------------------------------------

  /**
   * Attaches to M6's onTrackChange event.
   * Each new track object { artist, title, raw, source } is formatted and logged.
   *
   * @param {object} metadataEngine - object with an onTrackChange(callback) method (M6)
   */
  const subscribe = Logger.traced('M4', 'subscribe', (metadataEngine) => {
    metadataEngine.onTrackChange((track) => {
      let metadata;
      if (track && typeof track === 'object') {
        if (track.artist && track.title) {
          metadata = `${track.artist} - ${track.title}`;
        } else if (track.title) {
          metadata = track.title;
        } else {
          metadata = (track.raw || '').trim();
        }
      } else {
        metadata = (track || '').toString().trim();
      }
      Logger.log({
        ts: Date.now(), module: 'M4', fn: 'onTrackChange_handler',
        inputs: { track, metadata }, output: null, durationMs: 0, error: null,
      });
      _addToLog(metadata);
    });
    return true;
  });

  /**
   * Returns a copy of the log — ordered array of up to 50 strings, most recent first.
   */
  const getLog = Logger.traced('M4', 'getLog', () => {
    _loadLog();
    return [..._log];
  });

  /**
   * Registers a UI update callback. Fires after every log write,
   * passing the updated log array. Returns an unsubscribe function.
   * Supports multiple listeners — callers must unsubscribe on teardown.
   */
  const onLogUpdate = Logger.traced('M4', 'onLogUpdate', (callback) => {
    _logUpdateCbs.push(callback);
    return () => {
      _logUpdateCbs = _logUpdateCbs.filter(cb => cb !== callback);
    };
  });

  return { subscribe, getLog, onLogUpdate };
})();
